import React, { ComponentProps, useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal, TouchableOpacity } from 'react-native';

import { Box, Text } from '@components/Restyle';
import Icon from '@components/shared/Icon/Icon';
import { PopupTaskFilterDuedateV3 } from '@components/TaskList/PopupTaskFilterDuedateV3.web';
import { useOutsideClick } from '@hooks/useOutsideClick.web';
import { getScaledSize, SCALED_DIMENSIONS } from '@themes/theme';
import { localDateFromUTCDate } from '@utils/formatters/date';

type PositionTuple = ['left' | 'right', 'top' | 'bottom'];

type Position = PositionTuple | 'top' | 'bottom' | 'left' | 'right';

type Props = Omit<ComponentProps<typeof TouchableOpacity>, 'onPress'> & {
  title: string;
  onDismiss?: any;
  disabled?: boolean;
  hideTitle?: boolean;
  onSelectDate?: (dr: Date | undefined) => void;
  selectedDate: Date | undefined;
  onModalShow?: (visible: boolean) => void;
  boxProps: Partial<{
    position: Position;
    offset: [number, number] | number;
  }>;
};

export const MyTasksDueDatePicker = ({
  onDismiss,
  disabled,
  boxProps: { position, offset, ...popupPropsRest },
  title: xtitle,
  hideTitle = false,
  selectedDate,
  onSelectDate,
  onModalShow,
  accessibilityLabel,
}: Props) => {
  const [visible, setVisible] = useState(false);
  const { t: f } = useTranslation('format');
  const togglePopup = () => {
    onModalShow?.(!visible);
    setVisible((v) => !v);
  };

  const ref2 = useRef<HTMLDivElement>();

  const isEventPointInModal = (event: MouseEvent) => {
    const rect = ref2?.current?.getBoundingClientRect();
    return (
      rect &&
      event.x > rect.x &&
      event.x < rect.x + rect.width &&
      event.y > rect.y &&
      event.y < rect.y + rect.height
    );
  };
  const handleOutsideClick = useCallback((event: MouseEvent) => {
    if (!isEventPointInModal(event)) {
      onModalShow?.(false);
      setVisible(false);
    }
  }, []);

  const ref = useOutsideClick<HTMLDivElement>(handleOutsideClick);

  // Store the natural dimensions of the popup container
  const [layout, setLayout] = useState<
    { width: number; height: number } | undefined
  >();

  const [mouseOver, setMouseOver] = useState(false);

  const adjustBounds = (
    position: Props['boxProps']['position'] = 'bottom',
    offset: Props['boxProps']['offset'] = 0,
    client: DOMRect,
    popupLayout: { width: number; height: number }
  ) => {
    let modalHeight = 380;

    const [offsetLeftRight, offsetTopBottom] =
      typeof offset === 'number' ? [offset, offset] : offset;

    const normalizedPosition = (typeof position !== 'string' && position) ||
      ((position === 'left' || position === 'right') && [position, 'bottom']) ||
      ((position === 'top' || position === 'bottom') && ['left', position]) || [
        'left',
        'bottom',
      ];

    const [leftRight, topBottom] = normalizedPosition;
    const bounds = {
      top:
        topBottom === 'top'
          ? client.top - popupLayout.height - offsetTopBottom
          : client.top + client.height + offsetTopBottom,
      left:
        leftRight === 'left'
          ? client.left + client.width - popupLayout.width - offsetLeftRight
          : client.left + offsetLeftRight,
    };

    let top = client.bottom + offsetTopBottom;
    if (bounds.top + modalHeight + offsetTopBottom > window.innerHeight) {
      top = client.top - modalHeight - offsetTopBottom;
    }

    if (top < 0) {
      top = client.bottom + offsetTopBottom;
    }

    return { ...bounds, top };
  };

  const adjustedBounds =
    ref.current &&
    layout &&
    adjustBounds(position, offset, ref.current.getBoundingClientRect(), layout);

  const onModalDismiss = () => {
    onDismiss?.();
    onModalShow?.(false);
    setVisible(false);
  };

  const backgroundColor1 = () => {
    if (disabled) return 'grey02';
    if (mouseOver) return 'grey01';
    return 'white';
  };

  return (
    <Box ref={ref} mr='s'>
      <TouchableOpacity
        disabled={disabled}
        accessibilityLabel={accessibilityLabel || xtitle}
        onPress={(e) => {
          e.stopPropagation();
          togglePopup();
        }}>
        <Box
          width={hideTitle ? 32 : 123}
          height={getScaledSize(SCALED_DIMENSIONS.BUTTON_HEIGHT)}
          flexDirection='row'
          borderColor={disabled ? 'grey04' : 'grey02'}
          borderWidth={1}
          onMouseEnter={() => {
            setMouseOver(true);
          }}
          onMouseLeave={() => {
            setMouseOver(false);
          }}
          backgroundColor={backgroundColor1()}
          style={{ borderRadius: 40 }}
          alignItems='center'
          justifyContent='center'>
          <Icon
            variant='xxs'
            name='Calendar'
            color={disabled && !selectedDate ? 'grey04' : 'textSecondary'}
          />
          {!hideTitle && (
            <Text
              marginLeft='xxs'
              variant='webBodyHintSubline'
              color={disabled && !selectedDate ? 'grey04' : 'textSecondary'}>
              {selectedDate
                ? f('monthAndDayYearV3', {
                    val: localDateFromUTCDate(selectedDate),
                  })
                : 'Due Date'}
            </Text>
          )}
        </Box>
      </TouchableOpacity>

      {!layout && (
        <Box
          left='-200vw'
          position='absolute'
          visible={!layout}
          onLayout={({ nativeEvent: { layout } }) => {
            setLayout((curr) => curr ?? layout);
          }}>
          <PopupTaskFilterDuedateV3
            {...popupPropsRest}
            selectedDate={selectedDate}></PopupTaskFilterDuedateV3>
        </Box>
      )}
      <Modal
        visible={visible}
        transparent
        onDismiss={() => onModalDismiss()}
        onRequestClose={() => onModalDismiss()}>
        <Box
          ref={ref2}
          style={{ ...adjustedBounds }}
          width={layout?.width}
          onStartShouldSetResponder={() => true}
          onMoveShouldSetResponder={() => true}
          onResponderStart={(e) => e.stopPropagation()}
          onResponderRelease={(e) => e.stopPropagation()}
          onResponderMove={(e) => e.stopPropagation()}>
          <Box flexShrink={1} justifyContent='flex-end'>
            <PopupTaskFilterDuedateV3
              onClose={onModalDismiss}
              {...popupPropsRest}
              onSelectDate={(date) => {
                onSelectDate?.(date);
                onModalDismiss();
              }}
              selectedDate={selectedDate}></PopupTaskFilterDuedateV3>
          </Box>
        </Box>
      </Modal>
    </Box>
  );
};
